/* MAJA® Sportswear — comportamiento de componentes base.
   Sin dependencias. Módulo ES (type="module"). Registra:
   - <maja-dropdown>  listbox accesible con input oculto para formularios
   - <maja-tooltip>   apertura por teclado/touch además de hover (CSS)
   - <maja-snackbar-region> + window.MajaSnackbar.show({...})
   - <maja-mobile-menu> navegación por niveles del menú hamburguesa
   Los <details class="maja-toggle"> no necesitan JS (nativo).
   Motion: solo opacidad/color. Sin transforms. */

const ICONS = {
  'circle-check': '<path d="M21.801 10A10 10 0 1 1 17 3.335"/><path d="m9 11 3 3L22 4"/>',
  'circle-alert': '<circle cx="12" cy="12" r="10"/><line x1="12" x2="12" y1="8" y2="12"/><line x1="12" x2="12.01" y1="16" y2="16"/>',
  'info': '<circle cx="12" cy="12" r="10"/><path d="M12 16v-4"/><path d="M12 8h.01"/>',
  'triangle-alert': '<path d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3"/><path d="M12 9v4"/><path d="M12 17h.01"/>',
  'x': '<path d="M18 6 6 18"/><path d="m6 6 12 12"/>',
};
const svg = (name, size = 16) =>
  `<svg width="${size}" height="${size}" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">${ICONS[name] || ''}</svg>`;

/* ---------------- Dropdown ---------------- */
class MajaDropdown extends HTMLElement {
  connectedCallback() {
    this.trigger = this.querySelector('.maja-dropdown__trigger');
    this.list = this.querySelector('.maja-dropdown__list');
    this.input = this.querySelector('input[type="hidden"]');
    this.label = this.querySelector('.maja-dropdown__value');
    this.items = [...this.querySelectorAll('.maja-menu-item')];
    if (!this.trigger || !this.list) return;
    this.list.hidden = true;
    this.trigger.addEventListener('click', () => this.toggle());
    this.trigger.addEventListener('keydown', (e) => this.onTriggerKey(e));
    this.list.addEventListener('keydown', (e) => this.onListKey(e));
    this.items.forEach((it, i) => {
      it.addEventListener('click', () => this.select(i));
      it.addEventListener('mousemove', () => this.focusItem(i, false));
    });
    document.addEventListener('click', (e) => { if (!this.contains(e.target)) this.close(); });
  }
  get disabled() { return this.trigger.getAttribute('aria-disabled') === 'true'; }
  toggle() { this.hasAttribute('open') ? this.close() : this.open(); }
  open() {
    if (this.disabled) return;
    this.setAttribute('open', '');
    this.list.hidden = false;
    this.trigger.setAttribute('aria-expanded', 'true');
    const sel = this.items.findIndex((it) => it.getAttribute('aria-selected') === 'true');
    this.focusItem(sel >= 0 ? sel : 0, true);
  }
  close(focusTrigger = false) {
    if (!this.hasAttribute('open')) return;
    this.removeAttribute('open');
    this.list.hidden = true;
    this.trigger.setAttribute('aria-expanded', 'false');
    this.items.forEach((it) => it.classList.remove('is-focused'));
    if (focusTrigger) this.trigger.focus();
  }
  focusItem(i, moveFocus) {
    this.items.forEach((it, k) => it.classList.toggle('is-focused', k === i));
    this.focused = i;
    if (moveFocus) this.items[i]?.focus();
  }
  select(i) {
    const it = this.items[i];
    if (!it || it.getAttribute('aria-disabled') === 'true') return;
    this.items.forEach((x) => x.setAttribute('aria-selected', 'false'));
    it.setAttribute('aria-selected', 'true');
    const value = it.dataset.value ?? it.textContent.trim();
    if (this.input) { this.input.value = value; this.input.dispatchEvent(new Event('change', { bubbles: true })); }
    if (this.label) { this.label.textContent = it.textContent.trim(); this.label.classList.remove('maja-dropdown__placeholder'); }
    this.dispatchEvent(new CustomEvent('maja:change', { bubbles: true, detail: { value, label: it.textContent.trim() } }));
    this.close(true);
  }
  onTriggerKey(e) {
    if (['ArrowDown', 'ArrowUp', 'Enter', ' '].includes(e.key)) { e.preventDefault(); this.open(); }
  }
  onListKey(e) {
    const n = this.items.length;
    if (e.key === 'ArrowDown') { e.preventDefault(); this.focusItem((this.focused + 1) % n, true); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); this.focusItem((this.focused - 1 + n) % n, true); }
    else if (e.key === 'Home') { e.preventDefault(); this.focusItem(0, true); }
    else if (e.key === 'End') { e.preventDefault(); this.focusItem(n - 1, true); }
    else if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); this.select(this.focused); }
    else if (e.key === 'Escape' || e.key === 'Tab') { this.close(e.key === 'Escape'); }
  }
}
if (!customElements.get('maja-dropdown')) customElements.define('maja-dropdown', MajaDropdown);

/* ---------------- Tooltip ---------------- */
class MajaTooltip extends HTMLElement {
  connectedCallback() {
    const target = this.firstElementChild;
    if (!target) return;
    const bubble = this.querySelector('.maja-tooltip__bubble');
    if (bubble && !bubble.id) bubble.id = `maja-tip-${Math.random().toString(36).slice(2, 8)}`;
    if (bubble) target.setAttribute('aria-describedby', bubble.id);
    if (!target.hasAttribute('tabindex') && !/^(A|BUTTON|INPUT|SELECT|TEXTAREA)$/.test(target.tagName)) target.setAttribute('tabindex', '0');
    target.addEventListener('touchstart', () => this.classList.toggle('is-open'), { passive: true });
    this.addEventListener('keydown', (e) => { if (e.key === 'Escape') this.classList.remove('is-open'); });
  }
}
if (!customElements.get('maja-tooltip')) customElements.define('maja-tooltip', MajaTooltip);

/* ---------------- Snackbar ---------------- */
const SNACK_ICON = { success: 'circle-check', error: 'circle-alert', information: 'info', warning: 'triangle-alert', neutral: '' };
const MajaSnackbar = {
  region() {
    let r = document.querySelector('.maja-snackbar-region');
    if (!r) {
      r = document.createElement('div');
      r.className = 'maja-snackbar-region';
      r.setAttribute('role', 'region');
      r.setAttribute('aria-label', 'Notificaciones');
      document.body.appendChild(r);
    }
    return r;
  },
  /** show({ message, type: 'neutral'|'success'|'error'|'information'|'warning', duration: ms (0 = persistente), action: {label, onClick}, dismissible }) */
  show({ message, type = 'neutral', duration = 4000, action, dismissible = true } = {}) {
    const el = document.createElement('div');
    el.className = `maja-snackbar maja-snackbar--${type}`;
    el.setAttribute('role', type === 'error' || type === 'warning' ? 'alert' : 'status');
    el.setAttribute('aria-live', type === 'error' ? 'assertive' : 'polite');
    el.innerHTML = `${SNACK_ICON[type] ? svg(SNACK_ICON[type]) : ''}<span class="maja-snackbar__text"></span>`;
    el.querySelector('.maja-snackbar__text').textContent = message;
    if (action?.label) {
      const b = document.createElement('button');
      b.type = 'button'; b.className = 'maja-snackbar__action'; b.textContent = action.label;
      b.addEventListener('click', () => { action.onClick?.(); this.dismiss(el); });
      el.appendChild(b);
    }
    if (dismissible) {
      const c = document.createElement('button');
      c.type = 'button'; c.className = 'maja-snackbar__close'; c.setAttribute('aria-label', 'Cerrar'); c.innerHTML = svg('x');
      c.addEventListener('click', () => this.dismiss(el));
      el.appendChild(c);
    }
    this.region().appendChild(el);
    requestAnimationFrame(() => el.classList.add('is-visible'));
    if (duration > 0) el._timer = setTimeout(() => this.dismiss(el), duration);
    return el;
  },
  dismiss(el) {
    if (!el || !el.isConnected) return;
    clearTimeout(el._timer);
    el.classList.remove('is-visible');
    el.addEventListener('transitionend', () => el.remove(), { once: true });
    setTimeout(() => el.remove(), 400);
  },
};
window.MajaSnackbar = MajaSnackbar;

/* Snackbars declarativos: <maja-snackbar-trigger data-message="…" data-type="success"> dispara al click del hijo. */
document.addEventListener('click', (e) => {
  const t = e.target.closest('[data-maja-snackbar]');
  if (!t) return;
  MajaSnackbar.show({ message: t.dataset.majaSnackbar, type: t.dataset.majaSnackbarType || 'neutral' });
});
if (document.body?.hasAttribute('data-maja-cart-snackbar')) {
  document.addEventListener('variant:added', () => MajaSnackbar.show({ message: document.body.dataset.majaCartSnackbar || 'Agregado a la bolsa', type: 'success' }));
}

/* ---------------- Menú mobile (megaMenu drill-down) ----------------
   Vive dentro de <header-sidebar> (snippets/header-sidebar.liquid). Cada nivel es una vista completa:
   [data-maja-mm-open="id"] entra a la vista, [data-maja-mm-back] regresa. Al cerrar el drawer vuelve a Level 01. */
class MajaMobileMenu extends HTMLElement {
  connectedCallback() {
    if (this.wired) return; // el tema puede reconectar el drawer; evita duplicar listeners
    this.wired = true;
    this.stack = [];
    this.root = this.querySelector('[data-root]');
    this.drawer = this.closest('header-sidebar');
    this.scroller = this.closest('.maja-mm__panel');
    this.addEventListener('click', (e) => {
      const opener = e.target.closest('[data-maja-mm-open]');
      if (opener) return this.open(opener);
      if (e.target.closest('[data-maja-mm-back]')) this.back();
    });
    this.drawer?.addEventListener('dialog:after-hide', () => this.reset());
    this.drawer?.querySelector('[data-maja-mm-search]')?.addEventListener('click', () => this.openSearch());
  }
  /* El buscador del sidebar se comporta igual que la lupa: cierra el drawer y abre Doofinder
     disparando su trigger configurado ("header button.site-header__search-toggle"). */
  async openSearch() {
    await this.drawer?.hide?.();
    const trigger = document.querySelector('.header__search-link .site-header__search-toggle');
    if (!trigger) return;
    // Mientras el drawer está abierto el resto de la página queda inert y el navegador ignora .click();
    // esperamos (máx. ~10 frames) a que se libere antes de disparar el trigger.
    for (let i = 0; i < 10 && trigger.closest('[inert]'); i++) await new Promise(requestAnimationFrame);
    await new Promise(requestAnimationFrame);
    trigger.click();
  }
  get current() { return this.querySelector('.maja-mm__view:not([hidden])') || this.root; }
  swap(view, focusTarget) {
    this.current.hidden = true;
    view.hidden = false;
    if (this.scroller) this.scroller.scrollTop = 0;
    focusTarget?.focus({ preventScroll: true });
  }
  open(opener) {
    const view = document.getElementById(opener.dataset.majaMmOpen);
    if (!view || !this.contains(view)) return;
    this.stack.push({ view: this.current, opener });
    opener.setAttribute('aria-expanded', 'true');
    this.swap(view, view.querySelector('[data-maja-mm-back]'));
  }
  back() {
    const prev = this.stack.pop();
    if (!prev) return;
    prev.opener.setAttribute('aria-expanded', 'false');
    this.swap(prev.view, prev.opener);
  }
  reset() {
    this.stack.forEach(({ opener }) => opener.setAttribute('aria-expanded', 'false'));
    this.stack = [];
    if (this.current !== this.root) this.swap(this.root);
  }
}
if (!customElements.get('maja-mobile-menu')) customElements.define('maja-mobile-menu', MajaMobileMenu);

/* ---------------- Wishlist: icono del sistema ---------------- */
const MAJA_HEART = '<svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 18 18" fill="none" aria-hidden="true"><path d="M14.25 10.5C15.3675 9.405 16.5 8.0925 16.5 6.375C16.5 5.28098 16.0654 4.23177 15.2918 3.45818C14.5182 2.6846 13.469 2.25 12.375 2.25C11.055 2.25 10.125 2.625 9 3.75C7.875 2.625 6.945 2.25 5.625 2.25C4.53098 2.25 3.48177 2.6846 2.70818 3.45818C1.9346 4.23177 1.5 5.28098 1.5 6.375C1.5 8.1 2.625 9.4125 3.75 10.5L9 15.75L14.25 10.5Z" stroke="#030303" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>';
const MAJA_WISHLIST_SELECTOR = [
  '[class*="swym" i]', '[class*="wishlist" i]', '[id*="swym" i]', '[id*="wishlist" i]',
  '[aria-label*="wish" i]', '[aria-label*="deseo" i]', '[aria-label*="favorit" i]',
].join(',');

function majaSwapHeart(oldSvg) {
  if (!oldSvg || oldSvg.dataset.majaHeart) return;
  const holder = document.createElement('div');
  holder.innerHTML = MAJA_HEART;
  const next = holder.firstElementChild;
  const cls = oldSvg.getAttribute('class');
  if (cls) next.setAttribute('class', cls);
  ['width', 'height'].forEach((a) => {
    const v = oldSvg.getAttribute(a);
    if (v) next.setAttribute(a, v);
  });
  /* Grosor directo en el SVG: el viewBox es 18, así que a 22px un stroke de 2 se vería a 2.44. */
  const px = parseFloat(next.getAttribute('width')) || 18;
  const sw = (2 * 18 / px).toFixed(3);
  next.querySelectorAll('path').forEach((p) => {
    p.setAttribute('stroke-width', sw);
    p.setAttribute('fill', 'none');
  });
  next.dataset.majaHeart = '1';
  oldSvg.replaceWith(next);
}

function majaSwapWishlistIcons(root = document) {
  root.querySelectorAll('wk-icon[icon="wishlist"] svg').forEach(majaSwapHeart);
  root.querySelectorAll('product-card, .product-card').forEach((card) => {
    card.querySelectorAll(MAJA_WISHLIST_SELECTOR).forEach((node) => {
      if (node.closest('.product-card__actions, .product-card__info')) return;
      if (node.tagName === 'svg') majaSwapHeart(node);
      else node.querySelectorAll('svg').forEach(majaSwapHeart);
    });
  });
}

/* ----------------------------------------------------------------------------------------------------------------------
   Contador de colores "+N" del product item: el tema/app lo pinta como disco. Lo dejamos como texto plano.
   Va por JS porque el markup varía según quién renderice la tarjeta (snippet propio o app de color images).
   -------------------------------------------------------------------------------------------------------------------- */
const MAJA_MORE_FLAT = {
  width: 'auto', 'min-width': '0', height: '24px', padding: '0 2px',
  border: '0', 'border-radius': '0', background: 'none', 'background-color': 'transparent',
  'box-shadow': 'none', outline: 'none', color: '#454545',
  'font-size': '12px', 'line-height': '16px', 'font-weight': '500',
  display: 'inline-flex', 'align-items': 'center', 'justify-content': 'flex-start',
  'white-space': 'nowrap', overflow: 'visible', flex: '0 0 auto'
};

function majaFlattenMoreCounters(root = document) {
  const candidates = root.querySelectorAll('.color-swatch--more, .color-swatch:not([for]), [class*="swatch"]:not([for]), .product-card__swatches > *');
  candidates.forEach((el) => {
    if (el.dataset.majaFlatCounter) return;
    if (el.querySelector('input, img, svg')) return;
    if (!/^\+\s*\d+$/.test((el.textContent || '').trim())) return;
    el.dataset.majaFlatCounter = '1';
    Object.entries(MAJA_MORE_FLAT).forEach(([k, v]) => el.style.setProperty(k, v, 'important'));
  });
}

const majaRunSwap = () => { majaSwapWishlistIcons(); majaFlattenMoreCounters(); };
if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', majaRunSwap);
else majaRunSwap();
[100, 400, 1200, 2500].forEach((ms) => setTimeout(majaRunSwap, ms));
new MutationObserver(() => {
  clearTimeout(window.__majaHeartTimer);
  window.__majaHeartTimer = setTimeout(majaRunSwap, 60);
}).observe(document.documentElement, { childList: true, subtree: true });

export { MajaDropdown, MajaTooltip, MajaSnackbar, MajaMobileMenu };
